"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HeroIntroduction() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stats = [{
    value: "15+ YRS",
    label: "CNC Expertise"
  }, {
    value: "500+ UNITS",
    label: "Parts Stocked"
  }, {
    value: "24/7 TECH",
    label: "Field Support"
  }];
  return <section data-section-id="2446" id="hero-introduction" className="relative bg-background text-foreground overflow-hidden min-h-[720px] h-screen w-full">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696316/img/siemens_cnc_industrial_hero.png)] will-change-transform" style={{
      animation: "lumenSlowZoom 20s ease-out infinite alternate"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at center, transparent 35%, hsla(220,48%,12%,0.65) 85%, hsla(220,48%,12%,0.95) 100%)"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(35deg, hsla(183,100%,32%,0.15) 0%, hsla(183,100%,32%,0.05) 30%, transparent 65%)"
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 bg-background/30 pointer-events-none" aria-hidden="true" />

      {}
      <div className="relative z-10 h-full w-full">
        <div className="mx-auto max-w-7xl h-full px-6 sm:px-8 lg:px-12">
          <div className="relative h-full flex flex-col justify-center">
            {}
            <div ref={contentRef} className="max-w-3xl" style={{
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? "translateY(0)" : "translateY(24px)",
            transition: "opacity 1.2s ease-out, transform 1.2s ease-out"
          }}>
              {}
              <div className="flex items-center gap-3 mb-6 sm:mb-8">
                <span className="h-px w-10 bg-primary" />
                <span className="font-mono text-xs sm:text-sm uppercase text-primary" style={{
                letterSpacing: "0.2em"
              }}>Industrial Integration</span>
              </div>

              {}
              <h1 className="font-default font-bold text-foreground" style={{
              fontSize: "clamp(48px, 9vw, 160px)",
              lineHeight: "0.95",
              letterSpacing: "-0.04em"
            }}>
                Precision <br className="hidden sm:block" />
                CNC Logic.
              </h1>

              {}
              <p className="mt-8 sm:mt-10 text-base sm:text-lg text-foreground/85 max-w-[480px] leading-relaxed font-default">
                High-quality CNC integration, retrofitting, and specialized Siemens parts. Engineered for industrial performance and absolute reliability.
              </p>

              {}
              <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5">
                <Link to="/ContactUsPage">
                  <Button size="lg" className="h-12 px-7 text-sm uppercase tracking-widest font-bold rounded-md bg-primary text-primary-foreground hover:opacity-90 w-full sm:w-auto">
                    Contact an Expert
                  </Button>
                </Link>
                <Link to="/PartsCataloguePage"><Button variant="ghost" size="lg" className="h-12 px-7 text-sm uppercase tracking-widest font-bold rounded-md text-foreground hover:bg-foreground/10 hover:text-foreground border border-primary w-full sm:w-auto group">
                    View Spare Parts
                    <ArrowRight className="ml-1 size-4 transition-transform group-hover:translate-x-1 text-primary" />
                  </Button></Link>
              </div>
            </div>

            {}
            <div ref={statsRef} className="absolute left-0 bottom-8 sm:bottom-12 hidden md:flex flex-col gap-0" style={{
            opacity: statsVisible ? 1 : 0,
            transform: statsVisible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 1.4s ease-out 0.4s, transform 1.4s ease-out 0.4s"
          }}>
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex items-center gap-4 py-3 border-t border-foreground/15 first:border-t-0 min-w-[260px]">
                  <span className="font-mono text-xs text-primary" style={{
                letterSpacing: "0.15em"
              }}>
                    {stat.value}
                  </span>
                  <span className="h-px flex-1 bg-foreground/20" />
                  <span className="font-mono text-[10px] uppercase text-foreground/60" style={{
                letterSpacing: "0.2em"
              }}>
                    {stat.label}
                  </span>
                </div>)}
            </div>

            {}
            <div className="md:hidden mt-12 grid grid-cols-3 gap-3 border-t border-foreground/15 pt-6">
              {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col gap-1">
                  <span className="font-mono text-[10px] text-primary" style={{
                letterSpacing: "0.15em"
              }}>
                    {stat.value}
                  </span>
                  <span className="font-mono text-[9px] uppercase text-foreground/60" style={{
                letterSpacing: "0.18em"
              }}>
                    {stat.label}
                  </span>
                </div>)}
            </div>

            {}
            <div className="absolute right-0 bottom-8 sm:bottom-12 hidden md:flex flex-col items-center gap-4">
              <span className="font-mono text-[10px] uppercase text-foreground/60" style={{
              letterSpacing: "0.3em",
              writingMode: "vertical-rl",
              transform: "rotate(180deg)"
            }}>
                System_Scroll
              </span>
              <div className="relative h-16 w-px overflow-hidden bg-foreground/15">
                <span className="absolute left-0 top-0 block w-px bg-primary" style={{
                height: "50%",
                animation: "lumenScrollLine 2.5s ease-in-out infinite"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <style>{`
        @keyframes lumenSlowZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.12); }
        }
        @keyframes lumenScrollLine {
          0% { transform: translateY(-100%); }
          60% { transform: translateY(200%); }
          100% { transform: translateY(200%); }
        }
      `}</style>
    </section>;
}